<template>
  <div class="ui-page">
    <app-filter-toolbar>
      <app-select
        id="resident-leaves-employee"
        v-model="employee"
        design="new"
        :options="employeeOptions"
        :clearable="true"
        label="full_name"
        placeholder="Employee"
        aria-label="Employee"
        @search="onEmployeeSearch"
        @input="onFilterChange"
      />
      <app-select
        id="resident-leaves-status"
        v-model="statuses"
        design="new"
        :options="statusOptions"
        label="name"
        placeholder="Status"
        aria-label="Leave statuses"
        multiple
        @input="onFilterChange"
      />
      <app-select
        id="resident-leaves-type"
        v-model="types"
        design="new"
        :options="typeOptions"
        label="name"
        placeholder="Leave type"
        aria-label="Leave types"
        multiple
        @input="onFilterChange"
      />
      <app-select
        id="resident-leaves-department"
        v-model="departments"
        design="new"
        :options="departmentOptions"
        label="name"
        placeholder="Department"
        aria-label="Departments"
        multiple
        @input="onFilterChange"
      />
      <template v-if="hasFilters" #clear>
        <app-clear-filters-button design="new" icon-only @clear="clearFilters" />
      </template>
    </app-filter-toolbar>

    <app-content-skeleton v-if="loading" label="Loading leaves" :columns="columns" min-width="1200px" />
    <app-error-state v-else-if="loadError" :message="loadError" @retry="fetch" />
    <template v-else>
      <div v-if="groups.length" class="ui-stack">
        <app-card v-for="group in groups" :key="group.code" design="new" variant="table" header-density="compact" :title="group.title" :count="group.rows.length">
          <app-data-table design="new" density="dense" :columns="columns" :rows="group.rows" :loading="loading" row-key="id" min-width="1200px">
            <template #cell-employee="{ row }">
              <app-table-text :text="row.employee.full_name">
                <router-link :to="{ name: 'employees-profile', params: { id: row.employee.id } }" class="ui-link">{{ row.employee.full_name }}</router-link>
              </app-table-text>
            </template>
            <template #cell-period="{ row }">
              <app-table-text :text="row.from_date || row.to_date ? formatPeriod(row.from_date, row.to_date) : null" />
            </template>
            <template #cell-days="{ row }">{{ row.days }}</template>
            <template #cell-status="{ row }">
              <app-status-badge design="new" compact :label="row.period_status.name" :tone="periodTone(row.period_status.code)" />
            </template>
            <template #cell-description="{ row }">
              <app-table-text :text="row.description" />
            </template>
            <template #cell-actions="{ row }">
              <app-icon-button v-if="row.period_status.code !== 'cancelled'" label="Preview in schedule" @click="$refs.calendarPreview.open(row)">
                <i class="pi pi-calendar" aria-hidden="true" />
              </app-icon-button>
              <app-icon-button v-if="row.period_status.code === 'upcoming'" label="Change" tone="amber" @click="$refs.mutation.open(row, 'change')">
                <i class="pi pi-pencil" aria-hidden="true" />
              </app-icon-button>
              <app-icon-button v-if="row.period_status.code === 'upcoming'" label="Cancel leave" tone="red" @click="$refs.mutation.open(row, 'cancel')">
                <i class="pi pi-ban" aria-hidden="true" />
              </app-icon-button>
            </template>
          </app-data-table>
        </app-card>
      </div>
      <app-no-results v-else design="new" title="No leaves" text="" />
      <app-pagination
        v-if="groups.length"
        design="new"
        placement="embedded"
        :page="page"
        :pages="pages"
        :count="count"
        :visible-count="items.length"
        :page-size="pageSize"
        :loading="loading"
        @change="onPageChange"
      />
    </template>
    <leave-request-schedule-preview ref="calendarPreview" read-only :department-options="departmentOptions" />
    <leave-mutation-modal ref="mutation" name="resident-leave-mutation" :approve="true" :submit-request="submitMutation" @saved="fetch" />
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { geEmployeesLeaveTypes, getEmployeesDepartmentList, getEmployeesListSearch, getEmployeesResidentLeaves, postEmployeesLeaveMutation } from '@/http'
import LeaveRequestSchedulePreview from './LeaveRequestSchedulePreview.vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import LeaveMutationModal from './LeaveMutationModal.vue'
import { formatPeriod } from '@/views/employees/sales-leave/format'

export default {
  name: 'ResidentLeaves',
  components: { LeaveRequestSchedulePreview, LeaveMutationModal, AppContentSkeleton, AppErrorState, AppTableText },
  data() {
    return {
      employee: null,
      employeeOptions: [],
      types: [],
      typeOptions: [],
      departments: [],
      departmentOptions: [],
      statuses: [],
      statusOptions: [
        { code: 'upcoming', name: 'Upcoming' },
        { code: 'in_process', name: 'In progress' },
        { code: 'past', name: 'Completed' },
        { code: 'cancelled', name: 'Cancelled' },
      ],
      items: [],
      page: 1,
      pageSize: 20,
      pages: 1,
      count: 0,
      loading: false,
      loadError: '',
      fetchRequestId: 0,
      columns: [
        { key: 'employee', label: 'Employee', minWidth: '160px' },
        { key: 'period', label: 'Current dates', minWidth: '190px' },
        { key: 'days', label: 'Days', align: 'right', width: '70px' },
        { key: 'status', label: 'Status', minWidth: '120px' },
        { key: 'description', label: 'Description', minWidth: '180px' },
        { key: 'actions', label: 'Actions', width: '116px' },
      ],
    }
  },
  computed: {
    hasFilters() {
      return Boolean(this.employee || this.types.length || this.departments.length || this.statuses.length)
    },
    groups() {
      const groups = new Map()
      this.items.forEach(row => {
        const type = row.type || {}
        const code = type.code || type.name || 'other'
        if (!groups.has(code)) groups.set(code, { code, title: type.name || 'Other leave', rows: [] })
        groups.get(code).rows.push(row)
      })
      return Array.from(groups.values())
    },
  },
  created() {
    this.fetch()
    geEmployeesLeaveTypes().then(response => { this.typeOptions = response.data || [] })
    getEmployeesDepartmentList().then(response => { this.departmentOptions = response.data || [] })
  },
  beforeDestroy() {
    this.fetchRequestId += 1
  },
  methods: {
    clearFilters() {
      this.employee = null
      this.types = []
      this.departments = []
      this.statuses = []
      this.onFilterChange()
    },
    submitMutation(leave, kind, payload) { return postEmployeesLeaveMutation(leave.id, kind, payload) },
    formatPeriod,
    periodTone(code) {
      return { upcoming: 'blue', in_process: 'green', past: 'neutral' }[code] || 'neutral'
    },
    fetch() {
      const requestId = ++this.fetchRequestId
      this.loading = true
      this.loadError = ''
      const params = { page: this.page, page_size: this.pageSize }
      if (this.employee) params.employee = this.employee.id
      if (this.types.length) params.type = this.types.map(type => type.code).join(',')
      if (this.statuses.length) params.period_status = this.statuses.map(status => status.code).join(',')
      if (this.departments.length) params.department = this.departments.map(department => department.code).join(',')
      return getEmployeesResidentLeaves(params)
        .then(response => {
          if (requestId !== this.fetchRequestId) return
          this.items = response.data.results
          this.count = response.data.count
          this.pages = response.data.pages
        })
        .catch(() => {
          if (requestId === this.fetchRequestId) this.loadError = 'Unable to load leaves. Please try again.'
        })
        .finally(() => {
          if (requestId !== this.fetchRequestId) return
          this.loading = false
        })
    },
    onFilterChange() {
      this.page = 1
      this.fetch()
    },
    onPageChange(page) {
      this.page = page
      this.fetch()
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployees(loading, search)
    },
    searchEmployees: debounce(function (loading, search) {
      if (!search) {
        this.employeeOptions = []
        loading(false)
        return
      }
      getEmployeesListSearch({ search, resident: true })
        .then(response => { this.employeeOptions = response.data })
        .finally(() => loading(false))
    }, 350),
  },
}
</script>
